/**
 * Helpers for daily cron expressions: "minute hour * * *"
 */

export function parseDailyCron(cron) {
  const raw = String(cron || '').trim()
  if (!raw) return null

  const parts = raw.split(/\s+/)
  if (parts.length < 5) return null

  const [minuteStr, hourStr, dom, month, dow] = parts
  if (dom !== '*' || month !== '*' || dow !== '*') return null
  if (!/^\d+$/.test(minuteStr) || !/^\d+$/.test(hourStr)) return null

  const minute = Number(minuteStr)
  const hour = Number(hourStr)
  if (!Number.isInteger(minute) || minute < 0 || minute > 59) return null
  if (!Number.isInteger(hour) || hour < 0 || hour > 23) return null

  return { hour, minute }
}

export function buildDailyCron(hour, minute) {
  const h = Number(hour)
  const m = Number(minute)
  if (!Number.isInteger(h) || h < 0 || h > 23) {
    throw new Error('Invalid hour')
  }
  if (!Number.isInteger(m) || m < 0 || m > 59) {
    throw new Error('Invalid minute')
  }
  return `${m} ${h} * * *`
}

export function dailyCronToTimeValue(cron, fallback = '08:30') {
  const parsed = parseDailyCron(cron)
  if (!parsed) return fallback

  const hh = String(parsed.hour).padStart(2, '0')
  const mm = String(parsed.minute).padStart(2, '0')
  return `${hh}:${mm}`
}

export function timeValueToDailyCron(timeValue) {
  if (!timeValue || !/^\d{1,2}:\d{2}$/.test(timeValue)) return null

  const [hh, mm] = timeValue.split(':').map((part) => parseInt(part, 10))
  if (!Number.isInteger(hh) || !Number.isInteger(mm)) return null

  return buildDailyCron(hh, mm)
}

export function formatDailyCronLabel(cron, { timezone } = {}) {
  const parsed = parseDailyCron(cron)
  if (!parsed) return cron || '—'

  const date = new Date()
  date.setHours(parsed.hour, parsed.minute, 0, 0)
  const timeStr = date.toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit',
  })

  if (timezone) {
    return `Daily at ${timeStr} (${timezone})`
  }
  return `Daily at ${timeStr}`
}

/** Minutes since midnight for sorting daily cron schedules. */
export function dailyCronMinutes(cron, fallback = '08:30') {
  const parsed = parseDailyCron(cron) || parseDailyCron(timeValueToDailyCron(fallback))
  if (!parsed) return 24 * 60
  return parsed.hour * 60 + parsed.minute
}

export function sortByDailyCron(items, getCron, getFallback = () => '08:30') {
  return [...items].sort((a, b) => {
    const diff = dailyCronMinutes(getCron(a), getFallback(a)) - dailyCronMinutes(getCron(b), getFallback(b))
    if (diff !== 0) return diff
    return String(a.label || a.id || '').localeCompare(String(b.label || b.id || ''))
  })
}
